"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OAuthManager = exports.REDIRECT_URI = exports.REFRESH_TOKEN_STORAGE_KEY = void 0;
const appauth_1 = require("@openid/appauth");
const toolbox_1 = require("@streem/toolbox");
const redirect_based_handler_1 = require("../oauth/redirect_based_handler");
const errors_1 = require("../errors");
const logger_1 = require("../utils/logger");
// This local storage key is also referenced in the script src linked in logout-success.html
// If you change the string value you must also change it in that static file
exports.REFRESH_TOKEN_STORAGE_KEY = 'FUSIONAUTH_REFRESH_TOKEN';
exports.REDIRECT_URI = typeof window !== 'undefined' ? window.location.origin + '/redirect.html' : '';
class OAuthManager {
    constructor() {
        this.requestor = new appauth_1.FetchRequestor();
        this.handler = new redirect_based_handler_1.RedirectRequestHandler();
        this.tokenHandler = new appauth_1.BaseTokenRequestHandler(this.requestor);
    }
    performAuthorizationRequest(config, offlineAccess = false) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(
            // eslint-disable-next-line camelcase
            (_b = (_a = config.login_configuration) === null || _a === void 0 ? void 0 : _a.open_id) === null || _b === void 0 ? void 0 : _b.client_id, 'Company is not configured for OAuth');
            const authServiceConfig = yield this.getAuthServiceConfig(config);
            const request = new appauth_1.AuthorizationRequest({
                client_id: config.login_configuration.open_id.client_id,
                redirect_uri: exports.REDIRECT_URI,
                scope: offlineAccess ? 'openid email offline_access' : 'openid email',
                response_type: appauth_1.AuthorizationRequest.RESPONSE_TYPE_CODE,
                extras: {
                    idp_hint: config.login_configuration.open_id.idp_hint || '',
                    forgot_password_override: window.location.origin + '/forgot',
                },
            });
            yield this.handler.performAuthorizationRequest(authServiceConfig, request);
        });
    }
    pendingAuthorizationRequest() {
        return this.handler.pendingAuthorizationRequest();
    }
    completeAuthorizationRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            const authRequestResponse = this.handler.completeAuthorizationRequestSync();
            if (authRequestResponse === null || authRequestResponse === void 0 ? void 0 : authRequestResponse.response) {
                const { request, response, serviceConfiguration } = authRequestResponse;
                const tokenResponse = yield this.requestAccessToken(serviceConfiguration, request, response);
                (0, toolbox_1.invariant)(tokenResponse.idToken);
                if (tokenResponse.refreshToken) {
                    this.storeRefreshTokenConfig(request.clientId, tokenResponse.refreshToken, serviceConfiguration);
                }
                return tokenResponse;
            }
            else if (authRequestResponse === null || authRequestResponse === void 0 ? void 0 : authRequestResponse.error) {
                throw new errors_1.OAuthError(authRequestResponse.error.errorDescription || authRequestResponse.error.error);
            }
            return;
        });
    }
    refreshTokenAvailable() {
        const config = this.getRefreshTokenConfig();
        return config !== undefined;
    }
    authorizeWithRefreshToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const refreshConfig = this.getRefreshTokenConfig();
            if (!refreshConfig) {
                return;
            }
            const tokenRequest = new appauth_1.TokenRequest({
                client_id: refreshConfig.clientId,
                redirect_uri: exports.REDIRECT_URI,
                grant_type: appauth_1.GRANT_TYPE_REFRESH_TOKEN,
                refresh_token: refreshConfig.token,
            });
            const response = yield this.tokenHandler.performTokenRequest(refreshConfig.authorizationServiceConfiguration, tokenRequest);
            (0, toolbox_1.invariant)(response.idToken);
            if (response.refreshToken && response.refreshToken !== refreshConfig.token) {
                this.storeRefreshTokenConfig(refreshConfig.clientId, response.refreshToken, refreshConfig.authorizationServiceConfiguration);
            }
            return response;
        });
    }
    clearStorage() {
        window.localStorage.removeItem(exports.REFRESH_TOKEN_STORAGE_KEY);
    }
    requestAccessToken(config, request, response) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const tokenRequest = new appauth_1.TokenRequest({
                client_id: request.clientId,
                redirect_uri: request.redirectUri,
                grant_type: appauth_1.GRANT_TYPE_AUTHORIZATION_CODE,
                code: response.code,
                extras: {
                    // eslint-disable-next-line camelcase
                    code_verifier: (_b = (_a = request.internal) === null || _a === void 0 ? void 0 : _a.code_verifier) !== null && _b !== void 0 ? _b : '',
                },
            });
            return this.tokenHandler.performTokenRequest(config, tokenRequest);
        });
    }
    getAuthServiceConfig(config) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(
            // eslint-disable-next-line camelcase
            (_b = (_a = config.login_configuration) === null || _a === void 0 ? void 0 : _a.open_id) === null || _b === void 0 ? void 0 : _b.authorization_endpoint, 'Company is not configured for OAuth');
            const authorizationEndpoint = new URL(config.login_configuration.open_id.authorization_endpoint);
            return appauth_1.AuthorizationServiceConfiguration.fetchFromIssuer(authorizationEndpoint.origin, this.requestor);
        });
    }
    storeRefreshTokenConfig(clientId, token, authorizationServiceConfiguration) {
        const refreshConfig = JSON.stringify({
            token,
            clientId,
            authorizationServiceConfiguration,
        });
        window.localStorage.setItem(exports.REFRESH_TOKEN_STORAGE_KEY, refreshConfig);
    }
    getRefreshTokenConfig() {
        const json = window.localStorage.getItem(exports.REFRESH_TOKEN_STORAGE_KEY);
        if (!json || json.length < 0) {
            return;
        }
        try {
            const config = JSON.parse(json);
            (0, toolbox_1.invariant)(config.token && config.clientId && config.authorizationServiceConfiguration);
            return config;
        }
        catch (e) {
            logger_1.logger.error('Unable to parse refresh token config.', e);
            window.localStorage.removeItem(exports.REFRESH_TOKEN_STORAGE_KEY);
            return;
        }
    }
}
exports.OAuthManager = OAuthManager;
//# sourceMappingURL=oauth_manager.js.map